import { useMemo } from "react";
import { format, parseISO } from "date-fns";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  TooltipProps,
} from "recharts";
import { DateRange } from "react-day-picker";
import { useDailyOrderVolumeByDepot, DepotVolumeData } from "@/api/analytics";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";

interface Props {
  dateRange: DateRange;
}

// Define types for the transformed data
interface ChartDataItem {
  date: string;
  formattedDate: string;
  [key: string]: string | number | null;
}

// Helper function to get an array of unique depot IDs from data
const getUniqueDepots = (data: DepotVolumeData[] | undefined | null) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return [];
  }

  const uniqueDepots = new Set<string>();
  data.forEach((item) => {
    if (item?.depotId) {
      uniqueDepots.add(item.depotId);
    } else {
      uniqueDepots.add("unknown");
    }
  });
  return Array.from(uniqueDepots);
};

// Transform data for the chart
const transformData = (
  data: DepotVolumeData[] | undefined | null
): ChartDataItem[] => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return [];
  }

  // Group by date
  const groupedByDate = data.reduce<Record<string, ChartDataItem>>(
    (acc, curr) => {
      if (!acc[curr.date]) {
        acc[curr.date] = {
          date: curr.date,
          formattedDate: format(parseISO(curr.date), "MMM d"),
        };
      }

      const depotKey = curr.depotId ?? "unknown";
      const depotNameKey = `${depotKey}_name`;

      acc[curr.date][depotKey] = curr.volume;
      acc[curr.date][depotNameKey] = curr.depotName;

      return acc;
    },
    {}
  );

  // Convert to array and sort by date
  return Object.values(groupedByDate).sort((a, b) =>
    a.date.localeCompare(b.date)
  );
};

// Custom tooltip component
const CustomTooltip = ({
  active,
  payload,
  label,
}: TooltipProps<number, string>) => {
  if (active && payload?.length) {
    return (
      <Card className="p-2 bg-white shadow-md border">
        <CardContent className="p-2">
          <p className="font-bold">{label}</p>
          {payload.map((entry, index) => {
            const dataKey = entry.dataKey as string | undefined;
            const depotNameKey = `${dataKey}_name`;
            const depotName =
              typeof entry.payload === "object" &&
              entry.payload !== null &&
              depotNameKey in entry.payload
                ? String(
                    (entry.payload as Record<string, unknown>)[depotNameKey]
                  )
                : "Unknown Depot";

            return (
              <div key={`tooltip-${index}`} className="flex justify-between">
                <span style={{ color: entry.color }}>{depotName}: </span>
                <span className="font-semibold ml-2">{entry.value} orders</span>
              </div>
            );
          })}
        </CardContent>
      </Card>
    );
  }
  return null;
};

// Colors for the chart
const CHART_COLORS = [
  "#4f46e5", // indigo
  "#0ea5e9", // sky
  "#10b981", // emerald
  "#f59e0b", // amber
  "#ef4444", // red
  "#8b5cf6", // violet
  "#ec4899", // pink
  "#06b6d4", // cyan
];

export default function DailyOrderVolumeByDepot({ dateRange }: Props) {
  // Use the API hook
  const { data, isLoading, error } = useDailyOrderVolumeByDepot(dateRange);

  // Get unique depots for rendering bars
  const uniqueDepots = useMemo(() => getUniqueDepots(data), [data]);

  // Transform data for chart
  const chartData = useMemo(() => transformData(data), [data]);

  if (isLoading) {
    return (
      <div className="w-full h-[300px] flex items-center justify-center">
        <Skeleton className="w-full h-[300px]" />
      </div>
    );
  }

  if (error) {
    console.error("Error fetching depot volume data:", error);
    return (
      <div className="w-full h-[300px] flex items-center justify-center text-red-500">
        Failed to fetch data. Please try again.
      </div>
    );
  }

  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <div className="w-full h-[300px] flex items-center justify-center text-gray-500">
        No data available for the selected date range
      </div>
    );
  }

  return (
    <div className="w-full h-[350px]">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 10 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="formattedDate" />
          <YAxis />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          {uniqueDepots.map((depot, index) => (
            <Bar
              key={depot}
              dataKey={depot}
              stackId="a"
              fill={CHART_COLORS[index % CHART_COLORS.length]}
              name={
                data.find((d) => (d.depotId ?? "unknown") === depot)
                  ?.depotName ?? "Unknown Depot"
              }
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
